import { useEffect, useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { Overline, Reveal } from "./Reveal";

const GOOGLE_PROFILE_URL = "https://maps.google.com/?cid=13773054810176929617";

const GOOGLE_REVIEWS = {
    rating: 5.0,
    total: 22,
    reviews: [
        {
            author: "Sandeep Gupta",
            rating: 5,
            text: "Great experience, well trained staff, fruittherapy is awesome with good result",
            time: "Google review",
        },
        {
            author: "Pooja Sahoo",
            rating: 5,
            text: "The ambience and music was calming and soothing.",
            time: "Google review",
        },
        {
            author: "Komarashiva",
            rating: 5,
            text: "Service is excellent in reasonable price recommend to everyone....",
            time: "Google review",
        },
    ],
};

const Stars = ({ value = 0 }) => (
    <div className="flex gap-1" aria-label={`${value} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((i) => (
            <Star
                key={i}
                size={14}
                aria-hidden="true"
                className={
                    i <= Math.round(value)
                        ? "fill-gold text-gold"
                        : "text-gold/30"
                }
            />
        ))}
    </div>
);

const GoogleButton = ({ label, primary = false, testId }) => (
    <a
        href={GOOGLE_PROFILE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={`inline-flex items-center gap-3 px-8 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 ${
            primary
                ? "bg-gold text-emerald-950 hover:bg-gold-light"
                : "border border-gold/40 text-gold hover:border-gold hover:bg-gold/10"
        }`}
    >
        {label}
        <ArrowUpRight size={14} aria-hidden="true" />
    </a>
);

const Reviews = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`)
            .then((r) => r.json())
            .then(setData)
            .catch(() => setData({ configured: false }));
    }, []);

    const live = Boolean(data?.configured && data?.rating);
    const display = live ? data : GOOGLE_REVIEWS;

    return (
        <section
            id="reviews"
            data-testid="reviews-section"
            className="relative py-28 md:py-36"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="mb-16 text-center md:mb-20">
                    <Reveal>
                        <Overline centered>Guest Words</Overline>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-cream sm:text-5xl">
                            Reviewed on{" "}
                            <span className="italic text-gold-light">
                                Google
                            </span>
                        </h2>
                    </Reveal>
                </div>

                <Reveal className="mb-14 text-center">
                    <p
                        data-testid="google-rating-summary"
                        className="font-serif text-6xl text-cream md:text-7xl"
                    >
                        {display.rating}
                        <span className="text-2xl text-sage/70"> / 5</span>
                    </p>
                    <div className="mt-4 flex justify-center">
                        <Stars value={display.rating} />
                    </div>
                    <p className="mt-3 text-xs uppercase tracking-[0.3em] text-sage/70">
                        {display.total} Google reviews
                    </p>
                </Reveal>
                <div className="grid gap-8 md:grid-cols-3">
                    {display.reviews.map((review, i) => (
                        <Reveal key={i} delay={i * 0.12} className="h-full">
                            <figure
                                data-testid={`review-card-${i}`}
                                className="flex h-full flex-col border border-gold/15 bg-emerald-900/30 p-8 transition-colors duration-500 hover:border-gold/45"
                            >
                                <Stars value={review.rating} />
                                <blockquote className="mt-6 flex-1 font-serif text-lg italic leading-relaxed text-cream/90">
                                    &ldquo;{review.text}&rdquo;
                                </blockquote>
                                <figcaption className="mt-6 border-t border-gold/15 pt-5">
                                    <p className="text-sm tracking-wide text-cream">
                                        {review.author}
                                    </p>
                                    <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-sage/60">
                                        {review.time}
                                    </p>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
                <p className="mt-8 text-center text-[11px] uppercase tracking-[0.25em] text-sage/50">
                    Reviews sourced from Google
                </p>
                <Reveal delay={0.15}>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <GoogleButton
                            label="Read Google Reviews"
                            primary
                            testId="google-reviews-read-button"
                        />
                        <GoogleButton
                            label="Review Us on Google"
                            testId="google-reviews-write-button"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Reviews;
